import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';

// Define the type for the context value
interface OfflineContextType {
  isOffline: boolean;
}

// Create the Offline context
const OfflineContext = createContext<OfflineContextType | undefined>(undefined);

// Custom hook to use the context
export const useOffline = (): boolean => {
  const context = useContext(OfflineContext);

  if (!context) {
    throw new Error('useOffline must be used within an OfflineProvider');
  }
  return context.isOffline;
  
};

// OfflineProvider component to manage the offline state
interface OfflineProviderProps {
  children: ReactNode;
}

export const OfflineProvider: React.FC<OfflineProviderProps> = ({ children }) => {
  const [isOffline, setIsOffline] = useState<boolean>(!navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <OfflineContext.Provider value={{ isOffline }}>
      {children}
    </OfflineContext.Provider>
  );
};

export default OfflineProvider;